<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import OneButton from '@/common/components/molecules/button/OneButton.vue';
import CopyButton from '@/common/components/molecules/copyButton/CopyButton.vue';
import CodeEditor from '@/tool/components/codeEditor/CodeEditor.vue';
import { isEmpty } from 'lodash-es';
import { LOG_POS } from '../podsListDetail/types';
import { Emits, Props, setup } from './logViewer.setup';

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  height: '220px',
  useExternalBtn: true,
  displayLogTime: true,
  displayLogTitle: true,
  isCurrentLog: true,
});
const emit = defineEmits<Emits>();

const {
  viewerRef,

  onClickIjeon,
  onClickIhu,
  copyToClipboard,
  onClickExternal,
  options,
  visibleLog,
  copyableLog,
  logTitle,

  showMore,
  overflow,
  contentView,
  maxHeight,
} = setup(props, emit);
</script>

<template>
  <div class="log-viewer__toolbar">
    <slot name="pre-toolbar" />
    <slot name="toolbar">
      <one-button
        left-icon="icon-arrow-up"
        shape="border"
        :is-loading="props.isFetching"
        :disabled="(props.isCurrentLog && props.logType === LOG_POS.head) || isEmpty(props.log)"
        size="xsmall"
        @click="onClickIjeon"
      >
        {{ $t('WORD.BEFORE') }}
      </one-button>
      <one-button
        v-show="!props.previous"
        left-icon="icon-arrow-down"
        shape="border"
        :is-loading="props.isFetching"
        :disabled="(props.isCurrentLog && props.logType === LOG_POS.tail) || isEmpty(props.log)"
        size="xsmall"
        @click="onClickIhu"
      >
        {{ $t('WORD.AFTER') }}
      </one-button>
    </slot>
    <slot name="post-toolbar" />
  </div>

  <div class="log-viewer">
    <div class="log-viewer__title">
      <span class="log-viewer__title-text">
        {{ logTitle }}
      </span>
      <div class="log-viewer__title-actions">
        <copy-button @click="() => copyToClipboard(copyableLog)" />
        <base-button
          v-if="props.useExternalBtn"
          type="icon"
          @click="onClickExternal"
        >
          <ev-icon icon="icon-send-copy" />
        </base-button>
      </div>
    </div>

    <div
      ref="contentView"
      :class="[
        'log-viewer__content',
        {
          reverse: props.logType !== LOG_POS.head,
        },
      ]"
    >
      <p
        v-if="!visibleLog"
        class="log-viewer__content-placeholder"
      >
        {{ props.previous ? $t('MESSAGE.UI.KUBERNETES.POD.NO_PREVIOUS') : $t('MESSAGE.NO_DATA') }}
      </p>
      <code-editor
        v-else
        ref="viewerRef"
        :code="visibleLog"
        :options="options"
      />

      <div
        v-if="!showMore"
        :class="[
          'log-viewer__more-button-box',
          {
            reverse: props.logType !== LOG_POS.head,
          },
        ]"
      >
        <one-button
          :right-icon="props.logType !== LOG_POS.head ? 'icon-arrow-up' : 'icon-arrow-down'"
          size="xsmall"
          class="log-viewer__more-button"
          @click="() => (showMore = !showMore)"
        >
          {{ $t('WORD.UI.MONITORING.LOG.MORE') }}
        </one-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.log-viewer {
  width: 100%;
  height: calc(100% - 28px);
  border: 1px solid var(--color-gray-05-05);
  border-radius: 2px;

  :deep(.cm-scroller) {
    width: fit-content;
    overflow-x: unset;
  }

  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-gray-05-05);
  }

  &__title-actions {
    display: flex;
    gap: 10px;
  }

  &__toolbar {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__content {
    display: flex;
    position: relative;
    flex-direction: column;
    max-height: v-bind('maxHeight');
    transition: max-height 0.5s;

    .code-editor-wrapper {
      overflow-y: v-bind('overflow') !important;
    }

    &-placeholder {
      height: 56px;
      line-height: 56px;
      text-align: center;
    }

    &.reverse {
      flex-direction: column-reverse;
    }
  }

  &__more-button-box {
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 8px 0;
    border-top: 1px solid var(--color-gray-05-05);

    &.reverse {
      border-top: none;
      border-bottom: 1px solid var(--color-gray-05-05);
    }
  }
}
</style>
